import { QueryClient } from '@tanstack/react-query';
import {
  ActiveSuggestedAction,
  SUGGESTED_ACTION_TYPE,
  SuggestedActionsResponse,
  SuggestedActionType,
} from 'router/subrouters/DataRoom/models/Dashboard';
import {
  DEAL_NOTES_OPEN_VALUE,
  LEAD_BOARD_QUERY_PARAM,
  LEAD_DRAWER_OPTIONS,
  LeadDrawerOptions,
} from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/models';
import { SUGGESTED_ACTIONS_QUERY_KEY } from '../../hooks/useSuggestedActionsQuery';

export const removeActiveSuggestedActionFromData = (
  oldData: SuggestedActionsResponse,
  types: SuggestedActionType[],
) => ({
  ...oldData,
  active: oldData.active.filter((item) => !types.includes(item.type)),
});

export const removeActiveSuggestedAction = (
  queryClient: QueryClient,
  types: SuggestedActionType[] = [],
) => {
  queryClient.setQueriesData<SuggestedActionsResponse>(
    { queryKey: [SUGGESTED_ACTIONS_QUERY_KEY] },
    (oldData) => {
      if (!oldData) return oldData;

      return removeActiveSuggestedActionFromData(oldData, types);
    },
  );
};

export const isSecureLinkSuggestion = (type: SuggestedActionType) =>
  type === SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING;

export const isNoFaqsSuggestion = (type: SuggestedActionType) =>
  type === SUGGESTED_ACTION_TYPE.NO_FAQS;

export const isUnrepliedQuestionsSuggestion = (type: SuggestedActionType) =>
  type === SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS;

export const LEAD_BOARD_SUGGESTION_TYPES: SuggestedActionType[] = [
  SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES,
  SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES,
  SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES,
];

export const isLeadBoardSuggestion = (type: SuggestedActionType) =>
  LEAD_BOARD_SUGGESTION_TYPES.includes(type);

// Single unread message / lead note: open that lead's drawer on the given tab.
// The "multiple" case has no leadId, so it just lands on the board.
const buildLeadDrawerParams = (
  suggestion: ActiveSuggestedAction,
  tab: LeadDrawerOptions,
): Record<string, string> => {
  const { count = 1, leadId } = suggestion;

  if (count !== 1 || !leadId) return {};

  return {
    [LEAD_BOARD_QUERY_PARAM.LEAD_ID]: leadId,
    [LEAD_BOARD_QUERY_PARAM.OPEN]: tab,
  };
};

const LEAD_BOARD_SEARCH_BUILDERS: Partial<
  Record<
    SuggestedActionType,
    (suggestion: ActiveSuggestedAction) => Record<string, string>
  >
> = {
  [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]: (suggestion) =>
    buildLeadDrawerParams(suggestion, LEAD_DRAWER_OPTIONS.MESSAGES),
  [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]: (suggestion) =>
    buildLeadDrawerParams(suggestion, LEAD_DRAWER_OPTIONS.NOTES),
  [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]: () => ({
    [LEAD_BOARD_QUERY_PARAM.DEAL_NOTES]: DEAL_NOTES_OPEN_VALUE,
  }),
};

/**
 * Builds the leads-board search string that deep-links a suggested action to
 * the right drawer:
 * - single unread message -> open the lead's chat tab
 * - single unread lead note -> open the lead's notes tab
 * - unread deal notes -> open the deal-notes drawer
 * The "multiple" message/note cases just land on the board.
 */
export const getLeadBoardSuggestionSearch = (
  suggestion: ActiveSuggestedAction,
): string => {
  const params = LEAD_BOARD_SEARCH_BUILDERS[suggestion.type]?.(suggestion) ?? {};

  const search = new URLSearchParams(params).toString();

  return search ? `?${search}` : '';
};
